import React, { createContext, useContext, useMemo, useState } from 'react';
import { seedPurchases, type PurchaseOrder } from '../data/myOrders';

/**
 * One order in progress, grouped by the merchant it belongs to — a Talab cart
 * can hold a burger from one kitchen and fries from another, and each is a
 * separate order when it is placed.
 */
export interface CartLine {
  /** Stable per configuration, so the same dish ordered twice differently
   *  stays as two editable lines. */
  id: string;
  listingId: string;
  merchant: string;
  title: string;
  image: string;
  /** "Double patty · no pickles · + Extra patty" */
  summary: string;
  /** Price for a single one, with its extras included. */
  unitPrice: number;
  qty: number;
}
interface CartValue {
  lines: CartLine[];
  count: number;
  total: number;
  /** Grouped for checkout — one order per merchant. */
  byMerchant: {
    merchant: string;
    lines: CartLine[];
    total: number;
  }[];
  open: boolean;
  setOpen: (open: boolean) => void;
  add: (line: Omit<CartLine, 'id'>, openCart?: boolean) => void;
  update: (id: string, qty: number) => void;
  remove: (id: string) => void;
  clear: () => void;
  /** Everything this shopper has ordered, newest first. */
  orders: PurchaseOrder[];
  /** Turns the cart into one order per merchant and empties it. */
  placeOrders: () => PurchaseOrder[];
  cancelOrder: (id: string) => void;
}
const CartContext = createContext<CartValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState<PurchaseOrder[]>(() => seedPurchases());
  const add = (line: Omit<CartLine, 'id'>, openCart = true) => {
    setLines(prev => {
      // The exact same configuration just increases in quantity.
      const match = prev.find(l => l.listingId === line.listingId && l.summary === line.summary);
      if (match) return prev.map(l => l.id === match.id ? {
        ...l,
        qty: Math.min(20, l.qty + line.qty)
      } : l);
      return [...prev, {
        ...line,
        id: `${line.listingId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      }];
    });
    if (openCart) setOpen(true);
  };
  const update = (id: string, qty: number) => setLines(prev => qty <= 0 ? prev.filter(l => l.id !== id) : prev.map(l => l.id === id ? {
    ...l,
    qty
  } : l));
  const remove = (id: string) => setLines(prev => prev.filter(l => l.id !== id));
  const value = useMemo<CartValue>(() => {
    const count = lines.reduce((n, l) => n + l.qty, 0);
    const total = lines.reduce((n, l) => n + l.unitPrice * l.qty, 0);
    const order: string[] = [];
    const groups = new Map<string, CartLine[]>();
    lines.forEach(l => {
      if (!groups.has(l.merchant)) {
        groups.set(l.merchant, []);
        order.push(l.merchant);
      }
      groups.get(l.merchant)!.push(l);
    });
    const byMerchant = order.map(m => ({
      merchant: m,
      lines: groups.get(m)!,
      total: groups.get(m)!.reduce((n, l) => n + l.unitPrice * l.qty, 0)
    }));
    const placeOrders = () => {
      const now = Date.now();
      const created: PurchaseOrder[] = byMerchant.map((g, i) => ({
        id: `TL-${20500 + Math.floor(Math.random() * 400) + i}`,
        merchant: g.merchant,
        placedAt: now,
        status: 'received',
        lines: g.lines.map(({
          listingId,
          title,
          summary,
          image,
          unitPrice,
          qty
        }) => ({
          listingId,
          title,
          summary,
          image,
          unitPrice,
          qty
        })),
        total: g.total,
        payment: 'Paid by card',
        fulfilment: 'Delivery to your saved address'
      }));
      setOrders(prev => [...created, ...prev]);
      setLines([]);
      return created;
    };
    const cancelOrder = (id: string) => setOrders(prev => prev.map(o => o.id === id ? {
      ...o,
      status: 'cancelled',
      cancelledAt: Date.now(),
      cancelReason: 'Cancelled by you'
    } : o));
    return {
      lines,
      count,
      total,
      byMerchant,
      open,
      setOpen,
      add,
      update,
      remove,
      clear: () => setLines([]),
      orders,
      placeOrders,
      cancelOrder
    };
  }, [lines, open, orders]);
  return <CartContext.Provider value={value} data-id="element-5234" data-mp="1cpywud6sstba">{children}</CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}